import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import {
  AlertsFiltersGQL,
  AlertsFiltersQuery,
  AlertStatus,
  JustificationStatus,
  JustifyAlertsTableGQL,
  JustifyAlertsTableQuery,
  JustifyAlertsTableQueryVariables,
} from '../../../generated/graphql';
import { LazyLoadEvent } from 'primeng/api';
import { Filter, Pagination } from '../../utils/Pagination';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { QueryRef } from 'apollo-angular';
import { filter } from 'rxjs/operators';
import { Table } from 'primeng/table';
import { CallsService } from '../calls/calls.service';
import { RejectCallDialogComponent, RejectCallDialogResult } from '../calls/reject-call-dialog/reject-call-dialog.component';

export interface CallRow {
  alertId: number;
  callId: number;
  lawyerName: string;
  date: Date;
  judicialDistrict: string;
  alertType: string;
  justificationStatus: JustificationStatus;
  rejectionReason: string | null;
  // Keep reference to mutate status in-place
  _callRef: { justificationStatus: JustificationStatus; rejectionReason?: string | null };
}

@Component({
  selector: 'app-justify-alerts-table',
  templateUrl: './justify-alerts-table.component.html',
  styleUrls: ['./justify-alerts-table.component.scss'],
})
export class JustifyAlertsTableComponent implements AfterViewInit, OnDestroy {
  @ViewChild('table')
  table: Table;

  filters: AlertsFiltersQuery;
  pagination: Pagination;

  rows: CallRow[] = [];
  allRows: CallRow[] = [];

  filterNoShift = false;
  totalElements = 0;

  justificationStatusOptions = [
    { label: 'Pendiente', value: JustificationStatus.Pending },
    { label: 'Aceptado', value: JustificationStatus.Accepted },
    { label: 'Rechazado', value: JustificationStatus.Rejected },
  ];

  private alerts: JustifyAlertsTableQuery['alerts'] = { content: [], totalElements: 0 };
  private ready = false;
  private subscription: QueryRef<JustifyAlertsTableQuery, JustifyAlertsTableQueryVariables>;

  constructor(
    getFilters: AlertsFiltersGQL,
    private service: JustifyAlertsTableGQL,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private callsService: CallsService,
  ) {
    getFilters.fetch().subscribe(s => (this.filters = s.data));
  }

  ngAfterViewInit(): void {
    this.table.filters['status'] = [
      { value: [AlertStatus.Resolved], matchMode: 'contains', operator: 'and' },
    ];

    this.subscription = this.service.watch(
      {
        pagination: {
          size: 20,
          page: 0,
          columns: 'lastUpdate',
          direction: -1,
        },
      },
    );

    this.subscription.valueChanges
      .pipe(filter(s => s.error == undefined))
      .subscribe(s => {
        this.alerts = s.data.alerts;
        this.totalElements = s.data.alerts.totalElements ?? 0;
        this.allRows = this.buildRows(s.data.alerts);
        this.applyJustificationFilter();
      });

    this.ready = true;

    this.onLazyLoad(this.table.createLazyLoadMetadata());
  }

  ngOnDestroy() {
    this.subscription?.stopPolling();
  }

  private activeSortField: string | null = null;
  private activeSortOrder: number = 1;

  async onLazyLoad(event: LazyLoadEvent) {
    if (event.sortField === 'justificationStatus') {
      this.activeSortField = 'justificationStatus';
      this.activeSortOrder = event.sortOrder ?? 1;
      this.applyJustificationFilter();
      return;
    }
    this.activeSortField = event.sortField ?? null;
    this.activeSortOrder = event.sortOrder ?? 1;
    this.pagination = new Pagination(event);
    this.applyNoShiftFilter();
    await this.subscription.setVariables({ pagination: this.pagination });
  }

  toggleNoShiftFilter(): void {
    this.filterNoShift = !this.filterNoShift;
    if (!this.pagination || !this.subscription) return;
    this.applyNoShiftFilter();
    this.subscription.refetch({ pagination: { ...this.pagination, filters: [...this.pagination.filters] } });
  }

  private applyNoShiftFilter(): void {
    if (this.filterNoShift) {
      this.pagination.setFilter(new Filter('noShift', 'true', 'contains', 'and'));
    } else {
      this.pagination.removeFilter('noShift');
    }
  }

  updateElements() {
    if (this.ready) this.subscription.refetch();
  }

  onJustificationStatusFilter(values: JustificationStatus[]): void {
    this.activeJustificationFilter = values && values.length > 0 ? values : null;
    this.applyJustificationFilter();
  }

  private activeJustificationFilter: JustificationStatus[] | null = null;

  private readonly justificationStatusOrder: Record<JustificationStatus, number> = {
    [JustificationStatus.Pending]: 0,
    [JustificationStatus.Accepted]: 1,
    [JustificationStatus.Rejected]: 2,
  };

  private applyJustificationFilter(): void {
    let result: CallRow[];
    if (!this.activeJustificationFilter || this.activeJustificationFilter.length === 0) {
      result = this.allRows;
    } else {
      result = this.allRows.filter(r =>
        this.activeJustificationFilter.includes(r.justificationStatus)
      );
    }

    if (this.activeSortField === 'justificationStatus') {
      const order = this.justificationStatusOrder;
      const dir = this.activeSortOrder;
      result = [...result].sort((a, b) =>
        dir * (order[a.justificationStatus] - order[b.justificationStatus])
      );
    }

    this.rows = result;
  }

  private buildRows(alerts: JustifyAlertsTableQuery['alerts']): CallRow[] {
    const result: CallRow[] = [];
    const lawyerFilter = this.pagination?.filters.find(f => f.field === 'lawyerName')?.value?.toLowerCase();

    for (const alert of alerts.content) {
      if (!alert.allEvents) continue;
      for (const event of alert.allEvents) {
        if (!event || event.__typename !== 'Call') continue;
        const call = event as Extract<typeof event, { __typename: 'Call' }>;
        const lawyerName = `${call.lawyer.name} ${call.lawyer.lastName}`;
        if (lawyerFilter && !lawyerName.toLowerCase().includes(lawyerFilter)) continue;
        result.push({
          alertId: alert.id,
          callId: call.id,
          lawyerName,
          date: call.date,
          judicialDistrict: alert.judicialDistrict.name,
          alertType: alert.alertType?.name ?? alert.alias?.name ?? '',
          justificationStatus: call.justificationStatus,
          rejectionReason: call.rejectionReason ?? null,
          _callRef: call,
        });
      }
    }
    return result;
  }

  approve(row: CallRow): void {
    this.callsService.approveCall(row.callId).subscribe(result => {
      const updated = result?.data?.call;
      if (updated) {
        row.justificationStatus = updated.justificationStatus;
        row._callRef.justificationStatus = updated.justificationStatus;
      }
    }, () => {
      this.snackBar.open('Error al aprobar la llamada', 'Cerrar', {
        verticalPosition: 'top',
        horizontalPosition: 'right',
      });
    });
  }

  reject(row: CallRow): void {
    this.dialog
      .open(RejectCallDialogComponent, { width: '400px' })
      .afterClosed()
      .subscribe((result: RejectCallDialogResult | undefined) => {
        if (!result) return;
        this.callsService.rejectCall(row.callId, result.reason).subscribe(response => {
          const updated = response?.data?.call;
          if (updated) {
            row.justificationStatus = updated.justificationStatus;
            row.rejectionReason = updated.rejectionReason ?? null;
            row._callRef.justificationStatus = updated.justificationStatus;
            row._callRef.rejectionReason = updated.rejectionReason ?? null;
          }
        }, () => {
          this.snackBar.open('Error al rechazar la llamada', 'Cerrar', {
            verticalPosition: 'top',
            horizontalPosition: 'right',
          });
        });
      });
  }

  justificationStatusIcon(status: JustificationStatus): string {
    switch (status) {
      case JustificationStatus.Accepted:
        return 'check-circle-outline';
      case JustificationStatus.Rejected:
        return 'close-circle-outline';
      case JustificationStatus.Pending:
      default:
        return 'clock-outline';
    }
  }

  justificationStatusColor(status: JustificationStatus): string {
    switch (status) {
      case JustificationStatus.Accepted:
        return 'green-text';
      case JustificationStatus.Rejected:
        return 'red-text';
      case JustificationStatus.Pending:
      default:
        return 'grey-text';
    }
  }

  getJustificationTooltip(status: JustificationStatus, reason: string | null): string {
    if (status === JustificationStatus.Rejected) {
      return reason ? reason : 'Sin motivo';
    }
    return status;
  }
}
